import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { AdminApi } from "@app/shared/sdk";
import { environment } from "@env/environment";
import { SurveyModel } from "../survey.model";
import * as _ from "lodash";
import { HelperService } from "@app/main/core/services/helper.service";

interface Comment {
  question: string;
  comments: string[];
}

@Component({
  selector: "app-survey",
  templateUrl: "./survey.component.html",
  styleUrls: ["./survey.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SurveyComponent implements OnInit {
  @Input("survey") survey: SurveyModel = new SurveyModel();
  @Output() surveyChange = new EventEmitter<SurveyModel>();
  adminId: string;
  s3BucketUrl = environment.s3BucketUrl;

  tabs: { image: string; value: string }[] = [
    {
      image:
        "../../../../../../assets/images/survey-types/2-grid-horizontal.PNG",
      value: "vertical",
    },
    {
      image: "../../../../../../assets/images/survey-types/2-grid-vertical.PNG",
      value: "horizontal",
    },
    {
      image: "../../../../../../assets/images/survey-types/3-grid-view.PNG",
      value: "horizontal",
    },
    {
      image: "../../../../../../assets/images/survey-types/4-grid-view.PNG",
      value: "grid",
    },
    {
      image: "../../../../../../assets/images/survey-types/6-grid-view.PNG",
      value: "grid",
    },
    {
      image: "../../../../../../assets/images/survey-types/8-grid-view.PNG",
      value: "grid",
    },
    {
      image: "../../../../../../assets/images/survey-types/9-grid-view.PNG",
      value: "grid",
    },
  ];

  isCommentPagination = false;

  displayedChoices: any[] = [];

  constructor(
    private _adminApi: AdminApi,
    private domSanitizer: DomSanitizer,
    private helperService: HelperService
  ) {}

  ngOnInit(): void {
    this.adminId = this._adminApi.getCurrentToken().id;
    if (_.isEmpty(this.survey)) {
      this.survey = new SurveyModel();
    }
    if (
      this.survey.comments.every((el) =>
        el.comments.every((comment) => comment.value === undefined)
      )
    ) {
      this.initializeSurrveyComments();
    }
    
    this.updateChoicePages();
  }

  initializeSurrveyComments = () => {
    if (this.survey && _.isUndefined(this.survey.comments)) {
      this.survey.comments = Array.from({ length: 4 }, () => ({
        question: "",
        comments: Array.from({ length: 4 }, () => ({ value: "" })), // Corrected syntax for creating objects
      }));
    } else {
      // Initialize this.survey.comments with an array of 4 question objects
      this.survey.comments = Array.from({ length: 4 }, (_, questionIndex) => ({
        // Set the question property:
        // If this.survey exists and has a non-empty comments array,
        // and if the question at questionIndex exists, use that question.
        // Otherwise, set it to an empty string.
        question:
          this.survey &&
          this.survey.comments.length !== 0 &&
          this.survey.comments[questionIndex] &&
          this.survey.comments[questionIndex].question
            ? this.survey.comments[questionIndex].question
            : "",

        // Initialize the comments array with 4 comment objects
        comments: Array.from({ length: 4 }, (_, commentIndex) => ({
          // Set the value property:
          // If this.survey exists and has a non-empty comments array,
          // and if the comment at commentIndex in the current question exists and has a value, use that value.
          // Otherwise, set it to an empty string.
          value:
            this.survey &&
            this.survey.comments.length !== 0 &&
            this.survey.comments[questionIndex] &&
            this.survey.comments[questionIndex].comments[commentIndex] &&
            this.survey.comments[questionIndex].comments[commentIndex]
              ? this.survey.comments[questionIndex].comments[commentIndex]
              : "",
        })),
      }));
    }
  };

  uploadImage(
    $event,
    type: string,
    choice?: any,
    shouldCompress: boolean = true
  ) {
    if ($event && $event.target && $event.target.files.length !== 0) {
      let file = $event.target.files[0];

      const handleImageUpdate = (blob: Blob, base64?: string) => {
        const imageSrc = base64
          ? this.sanitizeUrl(base64)
          : this.sanitizeUrl(URL.createObjectURL(blob)); // <-- convert blob to URL

        if (type === "survey") {
          this.survey.image.file = blob;
          this.survey.image.src = imageSrc; // always string
        } else if (type === "choice") {
          const index = this.survey.choices.findIndex(
            (el) => el.label === choice.label
          );
          this.survey.choices[index].image.file = blob;
          this.survey.choices[index].image.src = imageSrc; // always string
        }

        this.surveyChange.emit(this.survey);
      };

      if (shouldCompress) {
        this.helperService
          .compressImage(file)
          .then((result) => {
            handleImageUpdate(result.compressedBlob, result.compressedBase64);
          })
          .catch((error) => {
            console.error("Error compressing image:", error);
          });
      } else {
        handleImageUpdate(file);
      }
    }
  }

  sanitizeUrl(url): SafeUrl {
    return this.domSanitizer.bypassSecurityTrustResourceUrl(url);
  }

  onTabChange($event) {
    const index = $event.index;
    const choicesLength = this.survey.choices.length;

    // Check if index is 0 or 1 and choices length is greater than 2
    if ((index === 0 || index === 1) && choicesLength > 2) {
      this.survey.choices.splice(2); // Remove excess choices
    } else if (index === 2) {
      // Check choices length on index 2
      if (choicesLength < 3) {
        this.pushChoices(1); // Add 1 choice
      } else if (choicesLength > 3) {
        this.survey.choices.splice(3); // Remove excess choices
      }
    } else if (index >= 3 && index <= 6) {
      const choices = [4, 6, 8, 9]; // Maximum choices for index 3 to 6

      // Check choices length based on index
      if (choicesLength < choices[index - 3]) {
        const additionalChoices = choices[index - 3] - choicesLength;
        this.pushChoices(additionalChoices); // Add additional choices
      } else if (choicesLength > choices[index - 3]) {
        this.survey.choices.splice(choices[index - 3]); // Remove excess choices
      }
    }

    // Update survey object with new values
    this.survey = {
      ...this.survey,
      displayType: this.tabs[index].value,
      tabIndex: index,
      choices: this.survey.choices,
      comments: this.survey.comments,
    };

    // Calculate valid max pages and reset current page if out of bounds
    const pageCount = Math.ceil(this.survey.choices.length / 4);
    if (this.currentChoicePage >= pageCount) {
      this.currentChoicePage = 0;
    }

    this.updateChoicePages();

    this.surveyChange.emit(this.survey); // Emit surveyChange event
  }

  choicePagesArray: any[] = [];
  currentChoicePage: number = 0;

  get choiceSliceStart() {
    return this.currentChoicePage * 4;
  }

  get choiceSliceEnd() {
    return (this.currentChoicePage * 4) + 4;
  }

  updateChoicePages() {
    const pageSize = 4;
    const len = this.survey && this.survey.choices ? this.survey.choices.length : 0;
    const pageCount = Math.ceil(len / pageSize);
    const pages = [];
    
    for (let i = 0; i < pageCount; i++) {
      const start = i * pageSize + 1;
      const end = Math.min((i + 1) * pageSize, len);
      
      pages.push({
        index: i,
        label: start === end ? `${start}` : `${start}-${end}`
      });
    }
    
    this.choicePagesArray = pages;
  }

  setChoicePage(pageIndex: number): void {
    this.currentChoicePage = pageIndex;
  }

  pushChoices(length: number) {
    const startingIndex = this.survey.choices.length + 1;

    // Generate an array of new choices with the correct labels
    const newChoices = Array.from({ length }, (_, i) => ({
      label: `Choice ${startingIndex + i}`,
      image: { file: null, src: null, isExpand: false },
    }));

    // Add the new choices to the survey.choices array
    this.survey.choices.push(...newChoices);
  }

  currentPage: number = 0;

  get currentQuestion(): Comment {
    return this.survey.comments[this.currentPage];
  }

  setPage(pageIndex: number): void {
    this.currentPage = pageIndex;
  }

  isActive(pageIndex: number): boolean {
    return this.currentPage === pageIndex;
  }

  onChoicesCommentTabChange($event) {
    this.isCommentPagination = $event.index === 1;
  }
}
